import { ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { phoneNumberValidators } from '../../../../../utils/const/profile';
import { KzModal } from '../../../../../kz-ui/ui/modal/kz-modal';
import { BehaviorSubject, merge, NEVER, of, Subscription, switchMap, throwError } from 'rxjs';
import { catchError, finalize, take, tap } from 'rxjs/operators';
import { PhoneCode } from '../../../../../shared/models/auth/phone-codes';
import { distinctHydraMember, filterUndefined } from '@adeo/ngx-kozikaza-api';
import { ProfessionalTenderService } from '@adeo/ngx-kozikaza-api/pro/misc';
import { ToastrService } from '../../../../../shared/services/utilities/toastr.service';
import { KzIconsRegistryService } from '../../../../../kz-ui/ui/icon/kz-icons-registry.service';
import { kzIconsCrosshair } from '../../../../../kz-ui/ui/icon/generated-icons/kzIcons-crosshair.icon';
import { kzIconsCompass } from '../../../../../kz-ui/ui/icon/generated-icons/kzIcons-compass.icon';
import { GoogleMapsUtilsService } from '../../../../../shared/google-maps-utils/google-maps-utils.service';
import PlaceResult = google.maps.places.PlaceResult;
import { UserStoreService } from '../../../../../shared/services/state-management/user-store.service';
import { ModalService } from '../../../../../utils/modals/modals.service';
import {
  AuthLightPopinComponent
} from '../../../../auth/auth-light/auth-light-popin.component';
import { ProfessionalTender } from '@adeo/ngx-kozikaza-api/pro/misc';

@Component({
  selector: 'app-pro-tender-form',
  templateUrl: './pro-tender-form.component.html',
  styleUrls: ['./pro-tender-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProTenderFormComponent implements OnInit, OnDestroy {
  public tenderProjects: Array<string> = [];
  public tenderProjects3D: Array<string> = [];
  public tenderProjects3DHelp: Array<string> = [];
  public placeholder = $localize`:@@pro.listing.banner.tender.placeholder:Tell us what your project is`;

  public proTenderForm: FormGroup;
  public submitted = false;
  public hasError = false;
  public serviceLocationForm: FormGroup;
  showServiceLocation = true;

  /* Phone Code */
  public codeSelected: PhoneCode;

  public _selectedAddress: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  @Input() tenderProject: string;

  @HostBinding('class.kz-pro-tender-form') classes = true;

  private subscription: Subscription;
  private submitSubscription: Subscription;

  private authModal: KzModal<AuthLightPopinComponent>;

  public add = $localize`:@@add:Add`;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly toastr: ToastrService,
    private readonly professionalTenderService: ProfessionalTenderService,
    private readonly cdr: ChangeDetectorRef,
    private readonly kzIconsRegistryService: KzIconsRegistryService,
    private readonly googleMapsUtilsService: GoogleMapsUtilsService,
    private readonly userStoreService: UserStoreService,
    private modal: KzModal<ProTenderFormComponent>,
    private readonly modalService: ModalService,
  ) {
    this.kzIconsRegistryService.registerIcons([kzIconsCrosshair, kzIconsCompass]);
  }

  get selectedAddress(): string {
    return this._selectedAddress.getValue();
  }
  set selectedAddress(val: string) {
    if (!val || val === '') {
      this.proTenderForm.get('postalCode').patchValue(null);
      this.proTenderForm.get('city').patchValue(null);
      this.proTenderForm.get('country').patchValue('FR');
      this.proTenderForm.get('city').markAsDirty();
    }
    this._selectedAddress.next(val);
  }

  onBlur(event) {
    if (!event.target.value) {
      this.selectedAddress = null;
    }
  }

  ngOnInit(): void {
    this.tenderProjects3DHelp = [
      //$localize`:@@pro.tender.projects.val26:3D plan - We design your plan for you - 29€`,
    ];
    this.tenderProjects3D = [
      ...this.tenderProjects3DHelp,
      $localize`:@@pro.tender.projects.val23:3D plan - Design simple room - 89€`,
      $localize`:@@pro.tender.projects.val24:3D plan - Design technical room - 139€`,
      $localize`:@@pro.tender.projects.val25:3D plan - Design multifunctional room - 189€`,
    ];
    this.tenderProjects = [
      $localize`:@@pro.tender.projects.val1:Complete renovation`,
      $localize`:@@pro.tender.projects.val2:Construction`,
      $localize`:@@pro.tender.projects.val3:flat renovation`,
      $localize`:@@pro.tender.projects.val4:House renovation`,
      $localize`:@@pro.tender.projects.val5:Interior design`,
      $localize`:@@pro.tender.projects.val6:Colour advice`,
      $localize`:@@pro.tender.projects.val7:Living room design`,
      $localize`:@@pro.tender.projects.val8:Kitchen design`,
      $localize`:@@pro.tender.projects.val9:Kitchen design`,
      $localize`:@@pro.tender.projects.val10:Bedroom design`,
      $localize`:@@pro.tender.projects.val11:Bathroom design`,
      $localize`:@@pro.tender.projects.val12:Bathroom design`,
      $localize`:@@pro.tender.projects.val13:Room redistribution`,
      $localize`:@@pro.tender.projects.val14:Decoration coaching`,
      $localize`:@@pro.tender.projects.val15:Attic design`,
      $localize`:@@pro.tender.projects.val16:Living room`,
      $localize`:@@pro.tender.projects.val17:Kitchen`,
      $localize`:@@pro.tender.projects.val18:Office`,
      $localize`:@@pro.tender.projects.val19:Shop`,
      $localize`:@@pro.tender.projects.val20:Children's room`,
      $localize`:@@pro.tender.projects.val21:Restaurant decoration`,
      $localize`:@@pro.tender.projects.val22:Restaurant layout`,
      ...this.tenderProjects3D
    ];
    const conceptionPlan3DHelpDescription = $localize`:@@pro.tender.projects.val26.desc:In a hurry to start decorating your home?

Kozikaza will draw your plan for you in less than 24 hours.
To achieve this, you will have to send us a drawing of your plan with the surfaces of the rooms. Your plan will be available directly on your Kozikaza account !
Make your request now and pay later.`;

    if(!!this.tenderProject && !this.tenderProjects.includes(this.tenderProject)) {
      this.tenderProjects.unshift(this.tenderProject);
    };

    this.serviceLocationForm = this.formBuilder.group({
      serviceAtHome: [false],
      serviceDistance: [false],
    }, {
      validators: atLeastOneCheckbox(),
    });

    this.proTenderForm = this.formBuilder.group({
      title: [this.tenderProject, [Validators.required]],
      phoneNumber: ['', [Validators.required, ...phoneNumberValidators]],
      description: ['', [Validators.required]],
      serviceLocation: this.serviceLocationForm
    });


    if(!!this.tenderProject) {
      if (this.tenderProjects3D.includes(this.tenderProject)) {
        this.showServiceLocation = false;
        this.serviceLocationForm.patchValue({
          serviceAtHome: false,
          serviceDistance: true
        })
      }
      if (this.tenderProjects3DHelp.includes(this.tenderProject)) {
        this.proTenderForm.patchValue({
          description: conceptionPlan3DHelpDescription,
        });
        this.proTenderForm.get('description').disable();
      }
    }

    this.subscription = merge(
      this.proTenderForm.get('title').valueChanges.pipe(
        tap((val) => {
          if(!!this.tenderProject && this.tenderProjects3D.includes(val)) {
            this.showServiceLocation = false;
            this.serviceLocationForm.patchValue({
              serviceAtHome: false,
              serviceDistance: true
            })
          } else {
            if (this.showServiceLocation === false) {
              this.serviceLocationForm.patchValue({
                serviceAtHome: false,
                serviceDistance: false
              });
            }
            this.showServiceLocation = true;
          }
        }),
        tap((val) => {
          if(!!this.tenderProject && this.tenderProjects3DHelp.includes(val)) {
            this.proTenderForm.patchValue({
              description: conceptionPlan3DHelpDescription,
            });
            this.proTenderForm.get('description').disable();
          } else {
            this.proTenderForm.get('description').enable();
          }
        }),
      ),
      this.proTenderForm.get('phoneNumber').valueChanges.pipe(
        tap(() => this.proTenderForm.get('phoneNumber').patchValue(this.proTenderForm.get('phoneNumber').value.replace(' ', ''))),
      ),
      this.proTenderForm.get('serviceLocation').get('serviceAtHome').valueChanges.pipe(
        tap((value) => {
          if (value) {
            this.proTenderForm.addControl('postalCode', new FormControl(''));
            this.proTenderForm.addControl('city', new FormControl('', [Validators.required]));
            this.proTenderForm.addControl('country', new FormControl('FR', [Validators.required]));
          } else {
            this.proTenderForm.removeControl('postalCode');
            this.proTenderForm.removeControl('city');
            this.proTenderForm.removeControl('country');
          }
          this.proTenderForm.updateValueAndValidity();
        }),
      )
    ).subscribe();
  }

  ngOnDestroy() {
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
    if (this.submitSubscription && !this.submitSubscription.closed) {
      this.submitSubscription.unsubscribe();
    }
  }

  onSubmit() {
    this.hasError = false;
    this.proTenderForm.markAsDirty();
    if (this.proTenderForm.valid) {
      // Enable description in order to get value if it has been disabled
      this.proTenderForm.get('description').enable();
      const phoneNumber = this.proTenderForm.controls.phoneNumber.value
        ? `${this.codeSelected.dial_code}${this.proTenderForm.controls.phoneNumber.value}` : null;
      const {serviceLocation, ...tenderTmp } = this.proTenderForm.value;
      const formTitle = this.proTenderForm.get('title').value;
      const payload = {
        ...tenderTmp,
        title: typeof formTitle === 'string' ? formTitle : formTitle['label'],
        phoneNumber,
        serviceAtHome: this.proTenderForm.get('serviceLocation').get('serviceAtHome').value,
        serviceDistance: this.proTenderForm.get('serviceLocation').get('serviceDistance').value,
      };

      this.proTenderForm.get('description').disable();

      this.submitSubscription = this.userStoreService.userStore.pipe(
        filterUndefined(),
        distinctHydraMember(),
        switchMap((user) => {
          if (!!user) {
            if (this.authModal) {
              this.authModal.close();
            }
            return of(user);
          } else {
            this.authModal = this.modalService.open<AuthLightPopinComponent>(
              AuthLightPopinComponent,{ closeButton: true, size: 'fullscreen'});
            this.authModal.componentRef.instance.title = $localize`:@@pro.tender.auth.title:Kozikaza`;
            return NEVER;
          }
        }),
        switchMap((user) => {
          this.submitted = true;
          return this.professionalTenderService.postTenders(payload).pipe(
            finalize(() => {
              this.submitted = false;
              this.proTenderForm.markAsPristine();
              this.cdr.markForCheck();
            }),
            catchError((e) => {
              this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
              return throwError(e);
            }),
          );
        }),
        switchMap((result: ProfessionalTender) => {
          const title = $localize`:@@pro.tender.confirmation.title:Validation of the request`;
          const content = $localize`:@@pro.tender.contentEmbed:Your request has been sent.<br>
Kozikaza is taking care of it now. You will be contacted directly by the pros we have selected for your project in the coming days.
<br><br>Watch your Kozikaza mailbox!`;
          return this.modalService.openInfoDialogOnClosed({ context: 'decorator', title, content},{ size: 'md'}).pipe(
            tap(() => {
              this.submitted = false;
              this.cdr.markForCheck();
              this.modal.close()
            }),
          );
        }),
      ).subscribe();
    } else {
      this.hasError = true;
    }
  }

  cancel() {
    this.modal.close();
  }

  isFieldInvalid(name: string, formName?: string): boolean {
    const form = formName ? this.proTenderForm.get(formName) : this.proTenderForm;
    if (!form) {
      return false;
    }

    const control = form.get(name);
    if (!control) {
      return false;
    }
    return (this.hasError || control.dirty || control.touched) && control.invalid;
  }

  onAddressSelected(result: PlaceResult) {
    this.selectedAddress = result.formatted_address;
    this.proTenderForm.get('postalCode').patchValue(this.googleMapsUtilsService.getPostCode(result) || '');
    this.proTenderForm.get('city').patchValue(this.googleMapsUtilsService.getCity(result));
    this.proTenderForm.get('country').patchValue(this.googleMapsUtilsService.getCountryShort(result) || 'FR');
    this.proTenderForm.get('city').markAsDirty();
  }

  selectAllContent($event) {
    $event.target.select();
  }

  geolocateMe() {
    this.googleMapsUtilsService.geolocateMe().pipe(
      take(1),
      tap((result) => {
        this.onAddressSelected(result);
      })
    ).subscribe();
  }

}

export function atLeastOneCheckbox() {
  return (formGroup: FormGroup) => {
    return ((formGroup.get('serviceAtHome') && formGroup.get('serviceAtHome').value) ||
      (formGroup.get('serviceDistance') && formGroup.get('serviceDistance').value))
      ? null
      : { requireCheckboxToBeChecked: true };
  };
}

